import {useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import DotsLoading from '../../../../../components/dotsLoading';
import ChatHeaderIcon from '../../../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';
import NoItemFoundPage from '../../../../../components/noItemFoundPage';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useGetAccountNumbers from '../../../../../services/hookService/getAccountNumbers/useGetAccountNumbers';
import useGetStoreListOrder from '../../../../../services/hookService/getStoreListOrder/useGetStoreListOrder';
import TabSelector from '../../../../myAccount/my-orders/components/TabSelector';
import AccountFillSection from './components/AccountFillSection';
import AddAccountNumberBottomSheet from './components/AddAccountNumberBottomSheet';
import MyStoreSettlementsItems from './components/MyStoreSettlementsItems';
import {
  myStoreSettlementDisplayConfig,
  MyStoreSettlementsTabsType,
  myStoreSettlementTabsConfig,
} from './settlementTabsConfig';

const MyStoreSettlementsOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get(
    'tab',
  ) as MyStoreSettlementsTabsType | null;

  const isValidTab = myStoreSettlementTabsConfig.some(
    tab => tab.key === tabFromUrl,
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const selectedTab: MyStoreSettlementsTabsType = useMemo(
    () =>
      isValidTab
        ? (tabFromUrl as MyStoreSettlementsTabsType)
        : 'pending_settlement',
    [tabFromUrl, isValidTab],
  );

  const {data, loaderRef, isFetchingNextPage, isLoading} =
    useGetStoreListOrder(selectedTab);

  const {data: accountNumbersData, isLoading: isAccountNumbersLoading} =
    useGetAccountNumbers();

  const storeOrders = useMemo(
    () =>
      data?.flatMap(order =>
        order.store_order.map(storeOrder => ({
          storeOrder,
          parentOrderGuid: order.guid,
        })),
      ) ?? [],
    [data],
  );

  // Sync tab changes to URL
  const handleTabChange = (tab: MyStoreSettlementsTabsType) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate({search: params.toString()}, {replace: true});
  };

  const selectedTabConfig = useMemo(
    () =>
      myStoreSettlementDisplayConfig.find(config => config.key === selectedTab),
    [selectedTab],
  );

  const hasAccount = Boolean(accountNumbersData?.bank_account_number);

  return (
    <main className="flex flex-col w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2"
            onClick={() => navigate('/my-store-info')}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">تسویه و واریز ها</span>
          </div>
        )}
        postComponent={() => (
          <div className="flex gap-3 ">
            <NotificationHeaderIcon />
            <ChatHeaderIcon />
          </div>
        )}
      />

      <section className="flex flex-col w-full h-full gap-3 px-6 py-3 overflow-y-auto">
        <TabSelector
          tabsConfig={myStoreSettlementTabsConfig}
          selectedTab={selectedTab}
          setSelectedTab={tab => handleTabChange(tab)}
          justifyContent="justify-start"
          gap="gap-4"
        />

        {isLoading && (
          <div className="flex items-center justify-center h-screen">
            <SpinnerLoading size={40} />
          </div>
        )}

        {!isLoading && (
          <div>
            {selectedTabConfig?.headerText && (
              <span className="block mt-2 text-sm font-normal text-neutral-500">
                {selectedTabConfig.headerText}
              </span>
            )}

            {selectedTabConfig?.key !== 'completed' && (
              <AccountFillSection
                hasAccount={hasAccount}
                setOpen={setIsOpen}
                accountNumbersData={accountNumbersData}
                isLoading={isAccountNumbersLoading}
              />
            )}

            <div className="flex flex-col gap-3 mt-3">
              {storeOrders.map(({storeOrder, parentOrderGuid}) => (
                <MyStoreSettlementsItems
                  key={storeOrder.guid}
                  storeOrder={storeOrder}
                  parentOrderGuid={parentOrderGuid}
                  selectedTab={selectedTab}
                />
              ))}
            </div>
          </div>
        )}

        {!isLoading && !storeOrders.length && <NoItemFoundPage />}

        <AddAccountNumberBottomSheet
          shouldOpen={isOpen}
          setShouldOpen={setIsOpen}
          accountNumbersData={accountNumbersData}
        />

        <div ref={loaderRef} className="mt-4 text-center">
          {isFetchingNextPage ? <DotsLoading /> : null}
        </div>
      </section>
    </main>
  );
};

export default MyStoreSettlementsOrders;
